import { Flex } from "antd";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import {
    getApiError,
    getEmployeePositionGetCurrentPositionThunk,
    getRouteInfoThunk,
    postSchedulerCreateRouteByPoints,
} from "tm-shared/api";
import { Route } from "tm-shared/types";

import { ROLE_CHILDREN_MAP } from "@/constants/domain/managerAgentVocabulary";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText } from "@/shared/ui/Typography";

import {
    getRouteEditorCapabilities,
    RouteEditorCapabilities,
} from "../RouteEditor.policy";
import { RouteEditorEdit } from "../RouteEditorEdit/RouteEditorEdit";

const StatusScreen = ({ children }: { children: ReactNode }) => (
    <>
        <Header />
        <MainContainer>
            <Flex vertical flex={1} align="center" justify="center">
                {children}
            </Flex>
        </MainContainer>
    </>
);

interface RouteEditorMyRouteProps {
    capabilities?: RouteEditorCapabilities;
}

export const RouteEditorMyRoute: FC<RouteEditorMyRouteProps> = ({
    capabilities: propCapabilities,
}) => {
    const dispatch = useAppDispatch();
    const creatingRef = useRef(false);

    const role = useProtectedUserRoleSelector();
    const capabilities = propCapabilities ?? getRouteEditorCapabilities(role);

    const { route, userId, firstName, lastName } = useAppSelector(
        (s) => ({
            route: s.user.route,
            userId: s.user.id,
            firstName: s.user.firstName,
            lastName: s.user.lastName,
        }),
        shallowEqual,
    );

    const [routeInfo, setRouteInfo] = useState<Route | null>(null);
    const [creating, setCreating] = useState(!route);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(!!route);

    const fetchRouteInfo = useCallback(
        async (routeId: string) => {
            try {
                setLoading(true);
                const { result } = await dispatch(
                    getRouteInfoThunk({ id: routeId }),
                ).unwrap();

                setRouteInfo(result);
            } catch (err) {
                const { message } = getApiError(err);
                setError(message);
            } finally {
                setLoading(false);
            }
        },
        [dispatch],
    );

    useEffect(() => {
        if (route) {
            fetchRouteInfo(route.uuid);
            return;
        }

        if (creatingRef.current) return;
        creatingRef.current = true;

        const createRoute = async () => {
            try {
                setCreating(true);

                const roleSlug = ROLE_CHILDREN_MAP[role] ?? role;
                const roleLabel = roleSlug?.toUpperCase() ?? "KAM";
                const routeName = `${roleLabel}: ${lastName} ${firstName}`;

                await postSchedulerCreateRouteByPoints({
                    name: routeName,
                    agentId: userId,
                    schedules: [],
                    roleSlug,
                });

                const { result } = await dispatch(
                    getEmployeePositionGetCurrentPositionThunk({
                        userId,
                        role,
                    }),
                ).unwrap();

                if (result.selfRoute) {
                    await fetchRouteInfo(result.selfRoute.uuid);
                }
            } catch {
                setError("Не вдалося створити маршрут. Спробуйте ще раз.");
            } finally {
                setCreating(false);
            }
        };

        createRoute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (creating) {
        return (
            <StatusScreen>
                <BlockLoader text="Маршрут створюється…" />
            </StatusScreen>
        );
    }

    if (error) {
        return (
            <StatusScreen>
                <PrimaryText textColor="error">{error}</PrimaryText>
            </StatusScreen>
        );
    }

    if (loading) {
        return (
            <StatusScreen>
                <Loading />
            </StatusScreen>
        );
    }

    if (!routeInfo) {
        return (
            <StatusScreen>
                <PrimaryText textColor="error">Маршрут не знайдено</PrimaryText>
            </StatusScreen>
        );
    }

    return (
        <RouteEditorEdit
            currentRoute={routeInfo}
            routeId={routeInfo.uuid}
            capabilities={capabilities}
        />
    );
};
